import { m } from '@/i18n';
import { errOf, ok, type Command, type Result } from './types';

/**
 * The user tag system's pure half (docs/FIELDS_AND_TAGS.md §6, settled
 * 2026-09-29). A tag is the string: a path of one or more segments joined by
 * `/` (`area/frontend/react`), compared case-insensitively through its FOLD
 * and stored as typed (after NFC). There is no tag entity, so everything here
 * is a function of strings: the path grammar, the nesting (a filter on `area`
 * matches `area/frontend`, never the reverse), and the `#word` parse that
 * turns typed text into tag applications.
 *
 * The grammar and the fold live here and nowhere else: every consumer (the
 * title parser, the picker, the filter criterion, the agent tools, the
 * explorer) goes through `parseTagPath`, `foldTag` and the token functions
 * below.
 *
 * Disjoint from the health catalog (`domain/tags.ts`, "signals" in the UI):
 * a user tag has no severity and never touches the dot (§5).
 */

/**
 * The characters a segment holds, in any script: a Unicode letter, a
 * combining mark, a Unicode number, `_` and `-`. No whitespace, punctuation,
 * symbol or emoji. A regex character-class body, for `u`-flag regexes only.
 */
const SEGMENT_CHARS = String.raw`\p{L}\p{M}\p{N}_\-`;
/** One segment. */
const SEGMENT = new RegExp(`^[${SEGMENT_CHARS}]+$`, 'u');
/** One path character: a segment character or the `/` between segments. */
const PATH_CHAR = new RegExp(`^[${SEGMENT_CHARS}/]$`, 'u');
/** The run of path characters at the start of a text: where a `#` token
 *  ends. */
const PATH_RUN = new RegExp(`^[${SEGMENT_CHARS}/]*`, 'u');
/** What may follow a token in the same word and leave the text with it: the
 *  punctuation that ends a sentence or a clause (`#urgent.`). */
const ONLY_PUNCTUATION = /^\p{P}*$/u;
/** A path needs a letter somewhere, so `#123` stays a number. */
const HAS_LETTER = /\p{L}/u;
/** The longest path accepted. A tag is a word, not prose. */
export const TAG_MAX_LENGTH = 100;

/** A parsed tag: the spelling as typed and its folded identity. */
export interface TagPath {
  tag: string;
  fold: string;
}

/**
 * Fold a path to its identity (case-insensitive compare, §6.1): NFC, then
 * `toLowerCase()`, then NFC again, because lower-casing can leave a sequence
 * that has a precomposed form (`T` plus a combining diaeresis lowers to `t`
 * plus the mark, whose NFC is one character). The one fold of the tag
 * system: computed here, never in SQL, so it does not depend on the
 * database's locale. Idempotent.
 */
export function foldTag(tag: string): string {
  return tag.normalize('NFC').toLowerCase().normalize('NFC');
}

/**
 * Parse typed input into a tag path: trims, normalizes to NFC, strips ONE
 * leading `#`, splits on `/`. Each segment is one or more letters, combining
 * marks, numbers, `_` or `-` in any script, no segment is empty, and the path
 * holds at least one letter. The spelling kept is the NFC form as typed. The
 * pure decider behind every apply.
 */
export function parseTagPath(input: string): Result<TagPath> {
  const trimmed = input.trim().normalize('NFC');
  const tag = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  if (tag.length === 0) return errOf('invalid_input', m('itemErrors.tag.nameNeeded'));
  if (tag.length > TAG_MAX_LENGTH) {
    return errOf('invalid_input', m('itemErrors.tag.tooLong', { max: String(TAG_MAX_LENGTH) }));
  }
  const segments = tag.split('/');
  if (segments.some((s) => s.length === 0)) {
    return errOf('invalid_input', m('itemErrors.tag.emptySegment'));
  }
  if (!segments.every((s) => SEGMENT.test(s))) {
    return errOf('invalid_input', m('itemErrors.tag.badCharacters'));
  }
  if (!HAS_LETTER.test(tag)) return errOf('invalid_input', m('itemErrors.tag.letterNeeded'));
  return ok({ tag, fold: foldTag(tag) });
}

/** The parent path of a fold, or null for a top-level tag. */
export function tagParent(fold: string): string | null {
  const at = fold.lastIndexOf('/');
  return at < 0 ? null : fold.slice(0, at);
}

/** Every ancestor path of a fold, nearest first (`a/b/c` -> `a/b`, `a`). */
export function tagAncestors(fold: string): string[] {
  const out: string[] = [];
  for (let p = tagParent(fold); p !== null; p = tagParent(p)) out.push(p);
  return out;
}

/**
 * Does a tag match a filter? The filter matches the tag itself and every
 * descendant (the Obsidian rule, §6.1): `area` matches `area/frontend`; a
 * filter on `area/frontend` does not match `area`. Both sides are folds.
 */
export function matchesTagFilter(fold: string, filterFold: string): boolean {
  return fold === filterFold || fold.startsWith(`${filterFold}/`);
}

/** Does any of an item's tags (spellings or folds) match the filter? */
export function carriesTag(tags: readonly string[], filterFold: string): boolean {
  return tags.some((t) => matchesTagFilter(foldTag(t), filterFold));
}

/**
 * Parse the `#word` tokens out of typed text (§6.3: entry is typing). A token
 * starts at the text's start or after whitespace and ends at the first
 * character a path cannot hold (whitespace, punctuation, a symbol), the rule
 * every hashtag follows: in a text without spaces it runs to the next
 * punctuation. What follows a token in the same word stays in the text,
 * unless it is only punctuation (`#urgent.` ends a sentence, and the `.`
 * leaves with the tag). A token that is not a valid path (`#123`, `#`) stays
 * in the text as written. The tokens leave the text, which is re-trimmed with
 * runs of spaces collapsed. Tags are returned in order, deduped by fold (the
 * first spelling wins).
 */
export function extractHashtags(text: string): { text: string; tags: string[] } {
  const tags: string[] = [];
  const seen = new Set<string>();
  const kept: string[] = [];
  for (const word of text.split(/(\s+)/)) {
    if (word.startsWith('#')) {
      const run = PATH_RUN.exec(word.slice(1))?.[0] ?? '';
      const parsed = parseTagPath(run);
      if (parsed.ok) {
        if (!seen.has(parsed.value.fold)) {
          seen.add(parsed.value.fold);
          tags.push(parsed.value.tag);
        }
        const rest = word.slice(1 + run.length);
        if (!ONLY_PUNCTUATION.test(rest)) kept.push(rest);
        continue;
      }
    }
    kept.push(word);
  }
  return { text: kept.join('').replace(/\s+/g, ' ').trim(), tags };
}

/** The width (1 or 2 UTF-16 units) of the path character that ends at `at`,
 *  or 0 when the character there cannot be in a path. */
function pathCharBefore(text: string, at: number): number {
  if (at < 1) return 0;
  const low = text.charCodeAt(at - 1);
  const width = at >= 2 && low >= 0xdc00 && low <= 0xdfff ? 2 : 1;
  return PATH_CHAR.test(text.slice(at - width, at)) ? width : 0;
}

/**
 * The `#token` the caret sits in, for the inline autocomplete (§6.3: typing
 * `#` opens the picker). Returns the token's bounds and its text after the
 * `#` (possibly empty, right after typing `#`), or null when the caret is not
 * inside a token. A token starts at the text's start or after whitespace and
 * runs over path characters (the grammar of `parseTagPath`, any script) up to
 * the caret; it ends where `extractHashtags` ends it.
 */
export function hashtagAt(
  text: string,
  caret: number,
): { start: number; end: number; query: string } | null {
  if (caret < 1 || caret > text.length) return null;
  let i = caret;
  while (i > 0) {
    const width = pathCharBefore(text, i);
    if (width === 0) break;
    i -= width;
  }
  if (i < 1 || text[i - 1] !== '#') return null;
  const hash = i - 1;
  if (hash > 0 && !/\s/.test(text[hash - 1]!)) return null;
  const end = caret + (PATH_RUN.exec(text.slice(caret))?.[0].length ?? 0);
  return { start: hash, end, query: text.slice(i, caret) };
}

/**
 * Replace the `#token` at `[start, end)` with the chosen tag and one trailing
 * space, so typing carries on. Returns the new text and the caret after it.
 */
export function completeHashtag(
  text: string,
  token: { start: number; end: number },
  tag: string,
): { text: string; caret: number } {
  const before = text.slice(0, token.start);
  const after = text.slice(token.end).replace(/^ /, '');
  const inserted = `#${tag} `;
  return { text: before + inserted + after, caret: before.length + inserted.length };
}

/** Does deciding this command need the item's worn tags (`WorkItemState.tagFolds`)? */
export function needsTagFolds(command: Command): boolean {
  return command.type === 'Tag' || command.type === 'Untag';
}

// --- The vote law (§6.4): every usage is a vote, and items decay -------------

/** One tag carried on one item the viewer can see: a vote, with the facts its
 *  weight reads. Gathered by `src/read/item-tags.ts`, ranked here. */
export interface TagVote {
  itemId: string;
  tag: string;
  fold: string;
  appliedBy: string;
  /** The item's last interaction (`work_items.updated_at`): the vote's clock. */
  updatedAt: Date;
  requesters: readonly string[];
  contributors: readonly string[];
  /** The `group:` grantees of the carrying item: the "teams" of the count. */
  groupRefs: readonly string[];
}

/** A ranked tag: the spelling most carriers wear, its score for this viewer on
 *  this item, how many items carry it and across how many teams, and whether
 *  any of its votes is still alive. */
export interface TagCandidate {
  tag: string;
  fold: string;
  score: number;
  count: number;
  teams: number;
  live: boolean;
}

/** Starting values (§6.4, SPEC §8 item 7: tuned on real use). */
export const TAG_VOTE = {
  /** The applier holds a seat on the carrying item. */
  seated: 1.0,
  /** The applier acts through a group or holds no seat. */
  unseated: 0.5,
  /** The viewer's own application, added on top. */
  own: 1.0,
  /** Days for a vote to halve. */
  halfLifeDays: 30,
  /** Below this a vote is dead; a tag whose every vote is dead is dormant. */
  dormantBelow: 0.05,
} as const;

const DAY_MS = 86_400_000;

/** A weight's time factor: `2^(-ageDays / halfLifeDays)`, 1 at `now` (and for
 *  a date after it), one half `halfLifeDays` later. The tag vote's shape, and
 *  the person shortlist's (`src/domain/counterparts.ts`). */
export function halfLifeFactor(at: Date, now: Date, halfLifeDays: number): number {
  const ageDays = Math.max(0, (now.getTime() - at.getTime()) / DAY_MS);
  return 2 ** (-ageDays / halfLifeDays);
}

/** The vote's time factor: `2^(-ageDays / halfLife)` on the item's last interaction. */
export function tagTimeFactor(updatedAt: Date, now: Date): number {
  return halfLifeFactor(updatedAt, now, TAG_VOTE.halfLifeDays);
}

/**
 * The standing factor of a vote: did the applier hold a seat on the carrying
 * item IN PERSON? Not an authority question - a ranking weight - and a seat
 * held through a group reads as "acts through a group" (§6.4), the lower
 * weight, so the raw ref comparison is the rule, not a shortcut.
 */
function seatWeight(v: TagVote): number {
  // eslint-disable-next-line no-restricted-syntax -- a vote weight on the applier's own seat, not an authority test (§6.4)
  const inPerson = v.requesters.includes(v.appliedBy) || v.contributors.includes(v.appliedBy);
  return inPerson ? TAG_VOTE.seated : TAG_VOTE.unseated;
}

/**
 * The distance factor of every carrying item relative to a focal item (§6.4:
 * "a tag on the parent, a sibling or a child weighs most; on a cousin less; on
 * an unrelated subtree least"). Decided from the parent edges of the upward
 * closure of the focal item and the carriers: for the nearest common
 * ancestor-or-self, the steps up from each side. (1,0) parent, (0,1) child,
 * (1,1) sibling: ×3; both within two steps (grandparent, aunt, cousin): ×2;
 * any common ancestor: ×1.5; nothing shared: ×1. Pure.
 */
export function tagDistanceFactors(
  focalId: string,
  carrierIds: readonly string[],
  edges: readonly { childId: string; parentId: string }[],
): Map<string, number> {
  const parentsOf = new Map<string, string[]>();
  for (const e of edges) {
    const held = parentsOf.get(e.childId);
    if (held) held.push(e.parentId);
    else parentsOf.set(e.childId, [e.parentId]);
  }
  const stepsUp = (from: string): Map<string, number> => {
    const steps = new Map<string, number>([[from, 0]]);
    let frontier = [from];
    for (let depth = 1; frontier.length > 0; depth += 1) {
      const next: string[] = [];
      for (const id of frontier) {
        for (const p of parentsOf.get(id) ?? []) {
          if (steps.has(p)) continue;
          steps.set(p, depth);
          next.push(p);
        }
      }
      frontier = next;
    }
    return steps;
  };
  const focal = stepsUp(focalId);
  const out = new Map<string, number>();
  for (const carrier of new Set(carrierIds)) {
    let best: [number, number] | null = null;
    for (const [node, sc] of stepsUp(carrier)) {
      const sf = focal.get(node);
      if (sf === undefined) continue;
      if (!best || sf + sc < best[0] + best[1]) best = [sf, sc];
    }
    out.set(carrier, distanceFactor(best));
  }
  return out;
}

function distanceFactor(steps: [number, number] | null): number {
  if (!steps) return 1;
  const [sf, sc] = steps;
  if (sf <= 1 && sc <= 1) return 3;
  if (sf <= 2 && sc <= 2) return 2;
  return 1.5;
}

/**
 * Rank the tags on the items the viewer can see (§6.4). A vote weighs its
 * applier's standing on the item (seated or not, plus the viewer's own use),
 * times the item's liveness, times its distance from the item being tagged
 * (`distance`, absent = no focal item). A tag's score is the sum of its
 * votes; a parent tag also ranks by its best descendant. The spelling offered
 * is the one most carriers wear. Standing never leaves this function: nothing
 * per person is returned (§6.5). Pure; sorted best first.
 */
export function rankTagCandidates(
  votes: readonly TagVote[],
  opts: { viewerRef: string; now: Date; distance?: ReadonlyMap<string, number> },
): TagCandidate[] {
  interface Acc {
    fold: string;
    score: number;
    items: Set<string>;
    teams: Set<string>;
    spellings: Map<string, number>;
    live: boolean;
  }
  const byFold = new Map<string, Acc>();
  for (const v of votes) {
    const seated = seatWeight(v);
    const time = tagTimeFactor(v.updatedAt, opts.now);
    const standing = seated + (v.appliedBy === opts.viewerRef ? TAG_VOTE.own : 0);
    const weight = standing * time * (opts.distance?.get(v.itemId) ?? 1);
    const acc =
      byFold.get(v.fold) ??
      byFold
        .set(v.fold, {
          fold: v.fold,
          score: 0,
          items: new Set(),
          teams: new Set(),
          spellings: new Map(),
          live: false,
        })
        .get(v.fold)!;
    acc.score += weight;
    acc.items.add(v.itemId);
    for (const g of v.groupRefs) acc.teams.add(g);
    acc.spellings.set(v.tag, (acc.spellings.get(v.tag) ?? 0) + 1);
    if (seated * time >= TAG_VOTE.dormantBelow) acc.live = true;
  }
  // A parent ranks by its best descendant (§6.4).
  const own = new Map([...byFold].map(([fold, acc]) => [fold, acc.score]));
  for (const [fold, score] of own) {
    for (const ancestor of tagAncestors(fold)) {
      const parent = byFold.get(ancestor);
      if (parent && score > parent.score) parent.score = score;
    }
  }
  return [...byFold.values()]
    .map((acc) => ({
      tag: [...acc.spellings].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]![0],
      fold: acc.fold,
      score: acc.score,
      count: acc.items.size,
      teams: acc.teams.size,
      live: acc.live,
    }))
    .sort((a, b) => b.score - a.score || a.fold.localeCompare(b.fold));
}

/** One node of the tag tree (§6.5, the explorer): a path segment, whether an
 *  item carries it itself, its live and dormant carrier counts (its own and
 *  its descendants'), and its children, live first. */
export interface TagTreeNode {
  fold: string;
  /** The spelling shown: the carriers' most common, or the path as folded
   *  for a node only its descendants carry. */
  tag: string;
  /** The last segment, for an indented row. */
  name: string;
  carried: boolean;
  live: number;
  dormant: number;
  score: number;
  children: TagTreeNode[];
}

/**
 * The explorer's tree (§6.5): every carried path and every ancestor of one,
 * each node counting the DISTINCT items in its subtree as live or dormant (an
 * item is dormant under a node when every vote it casts there has died). Live
 * nodes first, by score; dormant ones after, by path. Pure.
 */
export function tagTree(
  votes: readonly TagVote[],
  opts: { viewerRef: string; now: Date },
): TagTreeNode[] {
  const ranked = new Map(rankTagCandidates(votes, opts).map((c) => [c.fold, c]));
  const nodes = new Map<string, TagTreeNode & { liveIds: Set<string>; allIds: Set<string> }>();
  const node = (fold: string) => {
    const held = nodes.get(fold);
    if (held) return held;
    const c = ranked.get(fold);
    const tag = c?.tag ?? fold;
    const created = {
      fold,
      tag,
      name: tag.slice(tag.lastIndexOf('/') + 1),
      carried: c !== undefined,
      live: 0,
      dormant: 0,
      score: 0,
      children: [] as TagTreeNode[],
      liveIds: new Set<string>(),
      allIds: new Set<string>(),
    };
    nodes.set(fold, created);
    const parent = tagParent(fold);
    if (parent !== null) node(parent).children.push(created);
    return created;
  };
  for (const v of votes) {
    const seated = seatWeight(v);
    const alive = seated * tagTimeFactor(v.updatedAt, opts.now) >= TAG_VOTE.dormantBelow;
    for (const f of [v.fold, ...tagAncestors(v.fold)]) {
      const at = node(f);
      at.allIds.add(v.itemId);
      if (alive) at.liveIds.add(v.itemId);
      at.score = Math.max(at.score, ranked.get(v.fold)?.score ?? 0);
    }
  }
  for (const n of nodes.values()) {
    n.live = n.liveIds.size;
    n.dormant = n.allIds.size - n.liveIds.size;
  }
  const order = (list: TagTreeNode[]): TagTreeNode[] => {
    const plain = list.map((n) => {
      const {
        liveIds: _l,
        allIds: _a,
        ...rest
      } = n as TagTreeNode & {
        liveIds?: Set<string>;
        allIds?: Set<string>;
      };
      void _l;
      void _a;
      return { ...rest, children: order(rest.children) };
    });
    return plain.sort(
      (a, b) =>
        Number(b.live > 0) - Number(a.live > 0) ||
        (a.live > 0 ? b.score - a.score : 0) ||
        a.fold.localeCompare(b.fold),
    );
  };
  return order([...nodes.values()].filter((n) => tagParent(n.fold) === null));
}

/** What a picker row needs of a candidate: the spelling, the identity and the
 *  quiet adoption counts (§6.4: "on 340 items, 12 teams"). No score leaves the
 *  server: the order IS the ranking. */
export type TagOption = Pick<TagCandidate, 'tag' | 'fold' | 'count' | 'teams'>;

/**
 * The steps of a retag (§6.2: "edit the tag field of this tag on every item I
 * can see"), for the items the actor may act on: on each, every tag at `from`
 * or under it is removed and re-applied under `to` with its suffix kept
 * (`area/frontend` -> `ui/frontend` when `area` becomes `ui`). A tag whose new
 * path the item already wears is only removed (the two merge). Items in the
 * given order; pure.
 */
export function retagSteps(
  from: TagPath,
  to: TagPath,
  carriers: readonly { itemId: string; tags: readonly string[] }[],
): { itemId: string; command: Command }[] {
  const steps: { itemId: string; command: Command }[] = [];
  for (const carrier of carriers) {
    const worn = new Set(carrier.tags.map(foldTag));
    for (const tag of carrier.tags) {
      if (!matchesTagFilter(foldTag(tag), from.fold)) continue;
      // The suffix by segments, not by length: folding may change a
      // spelling's length (`İ` lowers to two code units).
      const next = [to.tag, ...tag.split('/').slice(from.fold.split('/').length)].join('/');
      steps.push({ itemId: carrier.itemId, command: { type: 'Untag', tag } });
      worn.delete(foldTag(tag));
      if (!worn.has(foldTag(next))) {
        steps.push({ itemId: carrier.itemId, command: { type: 'Tag', tag: next } });
        worn.add(foldTag(next));
      }
    }
  }
  return steps;
}
